import { memo, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import OTPComponent from "@zenith/components/ui/OTPComponent/otp-component/otp-component";
import { useUserDetailsContext } from "@zenith/context/global-context/hooks";
import { DEFAULT_COUNTRY_CODE } from "@zenith/data/constants";
import { AuthenticationApiService } from "@zenith/services/user";
import { OpenAPI } from "@zenith/services/user/core/OpenAPI";
import { request } from "@zenith/services/user/core/request";
import { SendOtpDTO } from "@zenith/services/user/models/SendOtpDTO";
import {
  completeRiskChallenge,
  subscribeRiskChallenge,
} from "@zenith/utils/riskChallenge";

export const RISK_CHALLENGE_TITLE = "Confirm it's you";
export const RISK_CHALLENGE_AUTHENTICATOR_TEXT =
  "For your security, enter the code from your authenticator app to continue.";
export const RISK_CHALLENGE_ERROR =
  "We could not verify the code. Please try again.";
const CODE_LENGTH = 6;

type Method = "loading" | "authenticator" | "sms";

/** The authenticator path: the MFA validate call with purpose RISK_CHALLENGE returns the token as xMfa. */
export async function validateAuthenticatorChallenge(
  otpCode: string
): Promise<string | null> {
  const response = await request<{ data?: { xMfa?: string } }>(OpenAPI, {
    method: "POST",
    url: "/api/mfa/totp/v2/validate",
    query: { purpose: SendOtpDTO.purpose.RISK_CHALLENGE },
    body: { otpCode },
    mediaType: "application/json",
  });
  return response?.data?.xMfa || null;
}

/**
 * Asks the user to confirm it is them when our fraud checks challenge a transfer or credential change
 * (see utils/riskChallenge). With two-factor authentication on it takes the authenticator code;
 * otherwise it sends an SMS code. Only Cancel closes it, so the waiting request is always answered.
 */
const RiskChallengeDialog = memo(function RiskChallengeDialog() {
  const { userDetailsContext } = useUserDetailsContext();
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState<Method>("loading");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => subscribeRiskChallenge(setOpen), []);

  useEffect(() => {
    if (!open) {
      return;
    }
    setMethod("loading");
    setCode("");
    setError(null);
    AuthenticationApiService.getVerification2Fa()
      .then((response) =>
        setMethod(response?.data?.authentication2fa ? "authenticator" : "sms")
      )
      .catch(() => setMethod("sms"));
  }, [open]);

  const verifyAuthenticator = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const token = await validateAuthenticatorChallenge(code);
      if (token) {
        completeRiskChallenge(token);
      } else {
        setError(RISK_CHALLENGE_ERROR);
      }
    } catch {
      setError(RISK_CHALLENGE_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  const onSmsVerified = (data: { xMfa?: string } | undefined) => {
    if (data?.xMfa) {
      completeRiskChallenge(data.xMfa);
    } else {
      setError(RISK_CHALLENGE_ERROR);
    }
  };

  const cancel = () => completeRiskChallenge(null);

  return (
    <Dialog open={open} onClose={() => undefined} maxWidth="xs" fullWidth>
      <DialogTitle>{RISK_CHALLENGE_TITLE}</DialogTitle>
      <DialogContent>
        {method === "loading" && <CircularProgress aria-label="Loading" />}
        {method === "authenticator" && (
          <>
            <p>{RISK_CHALLENGE_AUTHENTICATOR_TEXT}</p>
            <TextField
              autoFocus
              fullWidth
              label="Verification code"
              value={code}
              disabled={submitting}
              inputProps={{ inputMode: "numeric", maxLength: CODE_LENGTH }}
              onChange={(event) =>
                setCode(
                  event.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH)
                )
              }
            />
          </>
        )}
        {method === "sms" && (
          <OTPComponent
            phoneNumber={userDetailsContext.phoneNumber}
            countryCode={DEFAULT_COUNTRY_CODE}
            OTPPurpose={SendOtpDTO.purpose.RISK_CHALLENGE}
            onVerifySuccessWithData={onSmsVerified}
            onGoBack={cancel}
            backBtnInBottom={false}
            align="start"
          />
        )}
        {error && <p role="alert">{error}</p>}
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel}>Cancel</Button>
        {method === "authenticator" && (
          <Button
            variant="contained"
            disabled={submitting || code.length !== CODE_LENGTH}
            onClick={verifyAuthenticator}
          >
            Verify
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});

export default RiskChallengeDialog;
